import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { useBreadcrumbs } from "../../../context/breadcrumb/breadcrumb-context.tsx";
import PageShell from "../../shared/page-shell/page-shell.tsx";
import {
  TrainingCategoryLabel,
  type TrainingCategory,
} from "../../shared/training-category-label/training-category-label.tsx";
import { compareValues, DataTable } from "../../ui/table/data-table.tsx";
import type { DataTableColumn, SortDirection } from "../../ui/table/types.ts";
import { TablePagination } from "../../ui/table/table-pagination.tsx";
import { Select } from "../../ui/select/select.tsx";
import { DateRange } from "../../ui/date-range/date-range.tsx";

import styles from "./training-sessions-page.module.css";

type CategoryFilterValue = "all" | TrainingCategory;

export type TrainingSessionRow = {
  id: string;
  title: string;
  category: TrainingCategory;
  categoryLabel: string;
  startsAt: Date;
  dateTimeLabel: string;
} & Record<string, unknown>;

function formatDateTimeLabel(startsAt: Date): string {
  const datePart = startsAt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = startsAt.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} · ${timePart}`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function rangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  const as = startOfDay(aStart).getTime();
  const ae = startOfDay(aEnd).getTime();
  const bs = startOfDay(bStart).getTime();
  const be = startOfDay(bEnd).getTime();
  return as <= be && ae >= bs;
}

/** Session counts as on the calendar day of `startsAt` for range overlap. */
function sessionOverlapsRange(
  startsAt: Date,
  rangeStart: Date,
  rangeEnd: Date
): boolean {
  const day = startOfDay(startsAt);
  return rangesOverlap(day, day, rangeStart, rangeEnd);
}

function mockSession(
  id: string,
  title: string,
  category: TrainingCategory,
  categoryLabel: string,
  startsAt: Date
): TrainingSessionRow {
  return {
    id,
    title,
    category,
    categoryLabel,
    startsAt,
    dateTimeLabel: formatDateTimeLabel(startsAt),
  };
}

const MOCK_ROWS: TrainingSessionRow[] = [
  mockSession(
    "1",
    "Inclusive leadership fundamentals",
    "training",
    "Training",
    new Date(2026, 3, 5, 9, 0)
  ),
  mockSession(
    "2",
    "Security awareness refresher",
    "learning",
    "Learning",
    new Date(2026, 3, 8, 14, 0)
  ),
  mockSession(
    "3",
    "AWS Solutions Architect — prep",
    "workshop",
    "Workshop",
    new Date(2026, 3, 12, 10, 30)
  ),
  mockSession(
    "4",
    "Design systems at scale",
    "training",
    "Training",
    new Date(2026, 3, 15, 11, 0)
  ),
  mockSession(
    "5",
    "Anti-harassment (annual)",
    "learning",
    "Learning",
    new Date(2026, 3, 18, 8, 0)
  ),
  mockSession(
    "6",
    "First aid & workplace safety",
    "workshop",
    "Workshop",
    new Date(2026, 3, 22, 9, 0)
  ),
  mockSession(
    "7",
    "Effective feedback conversations",
    "training",
    "Training",
    new Date(2026, 3, 25, 15, 0)
  ),
  mockSession(
    "8",
    "GDPR & data handling",
    "learning",
    "Learning",
    new Date(2026, 3, 28, 12, 0)
  ),
  mockSession(
    "9",
    "Scrum Master (PSM I) cohort",
    "workshop",
    "Workshop",
    new Date(2026, 4, 2, 9, 30)
  ),
  mockSession(
    "10",
    "Presentation skills intensive",
    "training",
    "Training",
    new Date(2026, 4, 6, 13, 0)
  ),
  mockSession(
    "11",
    "Excel for analysts",
    "learning",
    "Learning",
    new Date(2026, 4, 9, 16, 0)
  ),
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "training", label: "Training" },
  { value: "learning", label: "Learning" },
  { value: "workshop", label: "Workshop" },
] as const;

const PAGE_SIZE = 5;

const TABLE_COLUMNS: DataTableColumn<TrainingSessionRow>[] = [
  {
    id: "session",
    header: "Session",
    accessor: "title",
    sortable: true,
  },
  {
    id: "dateTime",
    header: "Date & Time",
    accessor: "dateTimeLabel",
    getSortValue: (row) => row.startsAt,
    sortable: true,
  },
  {
    id: "category",
    header: "Category",
    accessor: "categoryLabel",
    getSortValue: (row) => row.category,
    sortable: true,
    render: (row) => (
      <TrainingCategoryLabel variant={row.category} label={row.categoryLabel} />
    ),
  },
];

function sortTrainingRows(
  rows: TrainingSessionRow[],
  sortKey: string,
  sortDir: Exclude<SortDirection, null>
): TrainingSessionRow[] {
  const col = TABLE_COLUMNS.find((c) => c.id === sortKey);
  if (!col) return rows;
  const getVal = (row: TrainingSessionRow) =>
    col.getSortValue != null
      ? col.getSortValue(row)
      : col.accessor != null
        ? row[col.accessor]
        : null;
  if (col.getSortValue == null && col.accessor == null) {
    return rows;
  }
  const copy = [...rows];
  copy.sort((a, b) => {
    const cmp = compareValues(getVal(a), getVal(b));
    return sortDir === "asc" ? cmp : -cmp;
  });
  return copy;
}

export default function TrainingSessionsPage() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [categoryFilter, setCategoryFilter] =
    useState<CategoryFilterValue>("all");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("dateTime");
  const [sortDir, setSortDir] = useState<Exclude<SortDirection, null>>("asc");

  useEffect(() => {
    setBreadcrumbs([
      { label: "Home", path: "/dashboard" },
      { label: "Training & Development" },
    ]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  const filtered = useMemo(() => {
    return MOCK_ROWS.filter((row) => {
      if (categoryFilter !== "all" && row.category !== categoryFilter) {
        return false;
      }
      if (startDate != null && endDate != null) {
        if (!sessionOverlapsRange(row.startsAt, startDate, endDate)) {
          return false;
        }
      }
      return true;
    });
  }, [categoryFilter, startDate, endDate]);

  const sortedFiltered = useMemo(
    () => sortTrainingRows(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir]
  );

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, startDate, endDate]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedFiltered.length / PAGE_SIZE) || 1
  );
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return sortedFiltered.slice(start, start + PAGE_SIZE);
  }, [sortedFiltered, safePage]);

  const sessionStatLabel =
    sortedFiltered.length === 1 ? "Session" : "Sessions";

  const handleSort = (key: string | undefined, dir: SortDirection) => {
    if (key == null || dir == null) {
      setSortKey("dateTime");
      setSortDir("asc");
      return;
    }
    setSortKey(key);
    setSortDir(dir);
  };

  return (
    <PageShell>
      <h1 className="visually-hidden">Training & Development</h1>
      <div className={styles.toolbar}>
        <div className={styles.filterSlot}>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as CategoryFilterValue)}
            options={[...CATEGORY_OPTIONS]}
          />
        </div>
        <div className={styles.filterSlot}>
          <DateRange
            label="Date range"
            startDate={startDate}
            endDate={endDate}
            onChange={([start, end]) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </div>
      </div>
      <div className={styles.tableSection}>
        <p
          className={clsx("sub-heading-sm", styles.totalStat)}
          aria-live="polite"
        >
          {sortedFiltered.length} {sessionStatLabel}
        </p>
        <DataTable<TrainingSessionRow>
          columns={TABLE_COLUMNS}
          data={pageRows}
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSort}
          aria-label="Training sessions"
        />
        <TablePagination
          page={safePage}
          pageSize={PAGE_SIZE}
          totalItems={sortedFiltered.length}
          onPageChange={setPage}
        />
      </div>
    </PageShell>
  );
}
